import { useEffect, useState, useMemo } from 'react';
import { useGetRegistryTransferredSyncInformationQuery, useGetShippingAddressQuery } from '@graphql/generated';
import { getLocalStorage } from '@shared/core';
import { useDisclosure } from '@withjoy/joykit';
import { useFeatureValue } from '@shared/core/featureFlags';
import { ShippingDialogView } from '@apps/admin/common/ShippingAddress/ShippingAddress';
import { useEventInfo } from '@shared/utils/eventInfo';

export const SHIPPING_ADDRESS_REMINDER_KEY = 'adressReminderClickedLater';

export const useShippingAddressReminder = ({
  eventId,
  eventHandle,
  isOtherCampaignThanRegistryOn
}: {
  eventId: string;
  eventHandle: string;
  isOtherCampaignThanRegistryOn: boolean;
}) => {
  const { data, loading: loadingShippingAddress } = useGetShippingAddressQuery({ variables: { eventId }, batchMode: 'fast' });
  const shippingAddress = data?.getRegistryEventData?.shippingAddress || null;
  const { eventInfo } = useEventInfo();
  const isRegistryEnabled = eventInfo?.productVerticals?.optOutOfRegistry === false;
  const { data: registryData, loading: loadingRegistryType } = useGetRegistryTransferredSyncInformationQuery({ variables: { eventHandle }, batchMode: 'fast' });
  const [isDismissed, setDismiss] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const adminRegistryEnforceShippingAddressEnabledTreatment = useFeatureValue('adminRegistryEnforceShippingAddressEnabled').value === 'treatment';
  const adminDashboardShippingAddressReminderEnabled = useFeatureValue('adminDashboardShippingAddressReminderEnabled').value === 'on';
  const adminRegistryEnforceShippingAddressEnabled = adminRegistryEnforceShippingAddressEnabledTreatment && adminDashboardShippingAddressReminderEnabled;

  const isTransferredOrSyncedRegistry: boolean = useMemo(() => registryData?.eventByName?.info.registry.some(reg => reg.isSyncingType || reg.isTransferredType) || false, [
    registryData
  ]);
  const localStorage = getLocalStorage();
  const isDialogDismissed = localStorage.getItem(SHIPPING_ADDRESS_REMINDER_KEY) === 'true';
  const dialogViewOpen: ShippingDialogView = isDialogDismissed ? 'form' : 'reminder';

  useEffect(() => {
    if (
      isRegistryEnabled &&
      !loadingShippingAddress &&
      !isOtherCampaignThanRegistryOn &&
      !loadingRegistryType &&
      !shippingAddress &&
      adminRegistryEnforceShippingAddressEnabled &&
      isTransferredOrSyncedRegistry &&
      !isOpen &&
      !isDialogDismissed
    ) {
      onOpen();
    }
  }, [
    isRegistryEnabled,
    isOtherCampaignThanRegistryOn,
    loadingRegistryType,
    registryData,
    onOpen,
    isOpen,
    isTransferredOrSyncedRegistry,
    isDialogDismissed,
    adminRegistryEnforceShippingAddressEnabled,
    loadingShippingAddress,
    shippingAddress
  ]);

  useEffect(() => {
    if (shippingAddress && isDialogDismissed) {
      localStorage.setItem(SHIPPING_ADDRESS_REMINDER_KEY, '');
    }
  }, [shippingAddress, isDialogDismissed, localStorage]);

  const shouldRenderBanner =
    isRegistryEnabled &&
    !loadingRegistryType &&
    !isOtherCampaignThanRegistryOn &&
    !shippingAddress &&
    !loadingShippingAddress &&
    !isDismissed &&
    isTransferredOrSyncedRegistry &&
    isDialogDismissed &&
    adminRegistryEnforceShippingAddressEnabled;

  const onCloseReminder = () => {
    const localStorage = getLocalStorage();
    localStorage.setItem(SHIPPING_ADDRESS_REMINDER_KEY, 'true');
    onClose();
  };

  return {
    shippingAddress,
    loadingShippingAddress,
    shouldRenderBanner,
    setDismissAddressBanner: setDismiss,
    isOpenShippingDialog: isOpen,
    onCloseShippingDialog: onCloseReminder,
    onOpenShippingDialog: onOpen,
    isTransferredOrSyncedRegistry,
    dialogViewOpen
  };
};
